import React, { useEffect, useState } from 'react';
import { FIELD } from './adminStyles';
import { SectionSwitch } from './CallSheetSectionSwitches';
import { audienceSummary, guestEmailProblem, parseGuestEmails } from '../../utils/calendarNotify';
import { appendGuestEmail, fetchKnownGuestEmails } from '../../utils/knownGuests';
import type { EntryNotify as EntryNotifyValue } from '../../utils/calendarEntries';

/**
 * Whether anyone gets an email about this entry, and who (22 Sep 2026;
 * rewritten 24 Sep 2026; guest suggestions 29 Sep 2026).
 *
 * Shown only when an entry is being CREATED. On an existing one the email has
 * already gone, and a control that looks live but changes nothing is worse
 * than no control — it invites someone to flip it and believe something
 * happened.
 *
 * **Notification is now off by default.** It used to be unconditional: every
 * entry emailed the owners, deliberately, because the whole calendar feature
 * exists so that a meeting cannot go in without Zeena knowing. What changed is
 * that the Week Ahead became the front page — both owners now see a new entry
 * the moment it is written, email or not — so the email is free to mean
 * something narrower: "this one needs saying out loud." Tom, after an 11am
 * recording session mailed the whole company: "a tick box for if something
 * needs a notification".
 *
 * The audience controls only exist once the switch is on. A guest field beside
 * a switch that is off is an invitation to type an address and assume it was
 * sent.
 */
export function EntryNotifyFields({
  value,
  onChange
}: {
  value: EntryNotifyValue;
  onChange: (patch: Partial<EntryNotifyValue>) => void;
}) {
  /**
   * Addresses used on a previous entry, so Jack is typed once and offered
   * ever after — see utils/knownGuests.ts. Fetched only once the switch is
   * on, because until then there is nothing to suggest against.
   */
  const [known, setKnown] = useState<string[]>([]);
  useEffect(() => {
    if (!value.notify || known.length > 0) return;
    let live = true;
    void fetchKnownGuestEmails().then(rows => {
      if (live) setKnown(rows);
    });
    return () => {
      live = false;
    };
  }, [value.notify, known.length]);

  /* Already in the field, so not worth offering again. */
  const chosen = new Set(parseGuestEmails(value.guestEmails));
  const suggestions = known.filter(email => !chosen.has(email.toLowerCase())).slice(0, 6);

  /**
   * Said as it is typed rather than held back until Add Entry.
   *
   * "jack" is a name, not an address, and the line above cheerfully read
   * "the owners, and jack, will get an email" — which was not true and was
   * only contradicted after the button was pressed. The check itself is
   * unchanged and still runs again on submit, and in the database after
   * that; this is only about saying it at the moment it is wrong.
   */
  const problem = guestEmailProblem(value.guestEmails);
  return <div className="mt-9 border-t border-paper/15 pt-8" data-id="element-1012" data-mp="1scigx9nhs62u">
      <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-1013" data-mp="3a93rwpyl17ah">
        <span className="label text-paper/65" data-id="element-1014" data-mp="3a6zrwj3t0sfh">Send a notification</span>
        <SectionSwitch label="Send a notification" on={value.notify} onToggle={() => onChange({
        notify: !value.notify
      })} data-id="element-1015" data-mp="3f6vt6al4a5rc" />
      </div>

      {!value.notify && <p className="label-sm mt-3 max-w-xl leading-relaxed text-paper/35" data-id="element-1016" data-mp="3nz7bta9zoowr">
          No email. It still goes in the diary, on the company calendar, and on the week ahead —
          quietly.
        </p>}

      {value.notify && <div className="mt-7 border-l border-paper/15 pl-6" data-id="element-1017" data-mp="30awdoevoh3c6">
          <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-1018" data-mp="309cxspdxouax">
            <span className="label-sm text-paper/40" data-id="element-1019" data-mp="35b28u515p7z0">Everyone on the team</span>
            <SectionSwitch label="Everyone on the team" on={value.notifyStaff} onToggle={() => onChange({
          notifyStaff: !value.notifyStaff
        })} data-id="element-1020" data-mp="fl71k9mczvn6" />
          </div>

          <p className="label-sm mt-3 max-w-xl leading-relaxed text-paper/35" data-id="element-1021" data-mp="j1tgrvaq1xbv">
            {audienceSummary(value.notifyStaff, value.guestEmails)}
          </p>

          <label className="mt-7 block" data-id="element-1022" data-mp="s3b1xs2zkp7f">
            <span className="label block text-paper/40" data-id="element-1023" data-mp="s4cclvg3b9om">Guests (optional)</span>
            <input value={value.guestEmails} onChange={event => onChange({
          guestEmails: event.target.value
        })} placeholder="jack@example.com, sam@example.com" autoComplete="off" spellCheck={false} className={FIELD} data-id="element-1024" data-mp="o16an000g4ny" />
          </label>

          {suggestions.length > 0 && <div className="mt-4" data-id="element-1025" data-mp="16s78fkpe9u5x">
              <span className="label-sm block text-paper/30" data-id="element-1026" data-mp="16rxjm0g35t1t">Used before</span>
              <div className="mt-2 flex flex-wrap gap-2" data-id="element-1027" data-mp="1kroys4ic9fhe">
                {suggestions.map(email => <button key={email} type="button" onClick={() => onChange({
            guestEmails: appendGuestEmail(value.guestEmails, email)
          })}
          /* Lower case and untracked on purpose: an address is not a
             label, and .label would upper-case it into something
             nobody could check against their own inbox. */ className="border border-paper/25 px-3 py-1 text-xs lowercase tracking-normal text-paper/60 transition-colors hover:border-paper/60 hover:text-paper" data-id="element-1028" data-mp="1ftdppvzvb908">
                    {email}
                  </button>)}
              </div>
            </div>}

          {problem ? <p role="alert" className="label-sm mt-4 max-w-xl leading-relaxed text-paper/70" data-id="element-1029" data-mp="1boba41bsr6ug">
              {problem}
            </p> : <p className="label-sm mt-3 max-w-xl leading-relaxed text-paper/30" data-id="element-1030" data-mp="xrem07n2c3h9">
              Anyone outside Portal Productions. They get the meeting, the time and the place — not
              the notes, and no link into Portal OS. Several at once is fine — separate them with a
              comma.
            </p>}
        </div>}
    </div>;
}